import React, { memo, useEffect } from 'react';
import { isEqual } from 'lodash';
import { Question } from 'src/types/helios/Question';
import { useNavigation } from 'src/context/NavigationContext';
import QuestionFormWizard from './QuestionFormWizard';

/**
 * Props interface for QuestionDetailsForm component
 */
interface QuestionDetailsFormProps {
  /** The question object to edit, null for new questions */
  question?: Question | null;
  /** Flag indicating if this is a copy operation */
  isCopyQuestion?: boolean;
  /** Callback function called when form is cancelled */
  onCancel?: () => void;
}

/**
 * QuestionDetailsForm component for creating and editing questions
 */
const QuestionDetailsForm: React.FC<QuestionDetailsFormProps> = memo(
  ({ question, onCancel, isCopyQuestion = false }) => {
    const { closeNavigation, openNavigation } = useNavigation();

    // Auto-collapse navigation when form opens, restore when form closes
    useEffect(() => {
      closeNavigation();

      // Cleanup function to restore navigation when component unmounts
      return () => {
        openNavigation();
      };
    }, [closeNavigation, openNavigation]);

    return (
      <QuestionFormWizard
        data-testid="question-details-form"
        question={question}
        isCopyQuestion={isCopyQuestion}
        onCancel={onCancel}
      />
    );
  },
);

const arePropsEqual = (
  prevProps: QuestionDetailsFormProps,
  nextProps: QuestionDetailsFormProps,
) => {
  return (
    isEqual(prevProps.question, nextProps.question) &&
    isEqual(prevProps.isCopyQuestion, nextProps.isCopyQuestion) &&
    isEqual(prevProps.onCancel, nextProps.onCancel)
  );
};

export default memo(QuestionDetailsForm, arePropsEqual);
